// src/components/PresensiTopHeaderControl.js
import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import GearIcon from "./_icons.js";
import AgendaCrossNavMenu from "../components/AgendaCrossNavMenu";


/**
 * PresensiTopHeaderControl (updated)
 *
 * - Icon-first buttons: Add / Scan / ShowDonasi / More
 * - Desktop: icon + label; Mobile: icon only
 * - All buttons share consistent size and visual rhythm; "More" button matched to others
 *
 * Icons (SVG) used:
 * - add:   https://lefyr2.com/square_plus_9243903_1_1fc13bce74.svg
 * - scan:  https://lefyr2.com/qrcode_8778055_6430d8ae73.svg
 * - eye:   https://lefyr2.com/eye_3917752_424ecd212c.svg
 * - eye_x: https://lefyr2.com/eye_crossed_3917753_49c8893b5b.svg
 */

export default function PresensiTopHeaderControl({
  agendaId,
  hasPreRegForm = false,
  onAddById,
  onScanQr,
  onToggleShowDonation,
  showDonation = false,
  onTogglePreRegVisibility,
  showPreReg = false,
  onExport,
  onGenerateReport,
  onOpenConfig,
  onRefresh
}) {
  const [open, setOpen] = useState(false);
  const [addCode, setAddCode] = useState("");
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 720 : false);
  const menuRef = useRef(null);
  const toggleRef = useRef(null);
  const [showMenu, setShowMenu] = useState(false);

  const ICON_REFRESH = "https://unpkg.com/heroicons@2.0.16/24/solid/arrow-path.svg"; // atau icon lain

  useEffect(() => {
    function onResize() {
      setIsMobile(window.innerWidth < 720);
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // close when clicking outside
  useEffect(() => {
    function onDocClick(e) {
      if (!open) return;
      if (menuRef.current && !menuRef.current.contains(e.target) && toggleRef.current && !toggleRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // keyboard: ESC to close
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
    }
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  function handleRefresh() {
    if (typeof onRefresh === "function") onRefresh();
  }

  function handleAddClick() {
    if (typeof onAddById === "function") onAddById();
  }

  function handleAddByCode(e) {
    e && e.preventDefault();
    const code = (addCode || "").trim();
    if (!code) return;
    if (typeof onAddById === "function") onAddById(code);
    setAddCode("");
    setOpen(false);
  }

  function handleScanClick() {
    if (typeof onScanQr === "function") onScanQr();
  }

  function handleToggleDonation() {
    if (typeof onToggleShowDonation === "function") onToggleShowDonation();
  }

  function handleTogglePreReg() {
    if (typeof onTogglePreRegVisibility === "function") onTogglePreRegVisibility();
    setOpen(false);
  }

  function handleExport() {
    if (typeof onExport === "function") onExport();
    setOpen(false);
  }

  function handleGenerateReport() {
    if (typeof onGenerateReport === "function") onGenerateReport();
    setOpen(false);
  }

  // icons
  const ICON_ADD = "https://lefyr2.com/square_plus_9243903_1_1fc13bce74.svg";
  const ICON_SCAN = "https://lefyr2.com/qrcode_8778055_6430d8ae73.svg";
  const ICON_EYE = "https://lefyr2.com/eye_3917752_424ecd212c.svg";
  const ICON_EYE_CROSS = "https://lefyr2.com/eye_crossed_3917753_49c8893b5b.svg";

  // common sizing
  const BTN_HEIGHT = "40px"; // consistent button height
  const BTN_PADDING_X = "10px";
  const ICON_SIZE = 18;

  // styles
  const styles = {
    container: { display: "flex", gap: "0.5rem", alignItems: "center" },

    // generic button base: square look for icon-only, or icon+label on desktop
    btnBase: {
      display: "inline-flex",
      gap: "0.6rem",
      alignItems: "center",
      padding: `0.35rem ${BTN_PADDING_X}`,
      borderRadius: "8px",
      border: "1px solid rgba(0,0,0,0.08)",
      background: "#fff",
      cursor: "pointer",
      fontSize: "0.95rem",
      lineHeight: 1,
      height: BTN_HEIGHT,
      minHeight: BTN_HEIGHT,
      boxSizing: "border-box",
      color: "#0b2f4a",
    },

    // primary variant: white background, colored border and colored label
    btnPrimary: {
      display: "inline-flex",
      gap: "0.6rem",
      alignItems: "center",
      padding: `0.35rem ${BTN_PADDING_X}`,
      borderRadius: "8px",
      border: "2px solid rgba(0,0,0,0.08)",
      background: "#1f64d0",
      color: "#fff",
      cursor: "pointer",
      fontSize: "0.95rem",
      lineHeight: 1,
      height: BTN_HEIGHT,
      minHeight: BTN_HEIGHT,
      boxSizing: "border-box",
      fontWeight: 700,
    },

    // toggle active style (for show/hide)
    btnActive: {
      background: "#aaa",
      color: "#fff",
    },

    // icon size
    iconOnly: { width: ICON_SIZE, height: ICON_SIZE, display: "inline-block", verticalAlign: "middle" },

    // label only on desktop
    labelDesktop: { display: isMobile ? "none" : "inline-block" },

    // more button will reuse btnBase so it matches visually
    moreBtn: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: BTN_HEIGHT,
      height: BTN_HEIGHT,
      minWidth: BTN_HEIGHT,
      padding: 0,
      borderRadius: "8px",
      border: "1px solid rgba(0,0,0,0.08)",
      background: "#fff",
      cursor: "pointer",
      boxSizing: "border-box",
      fontSize: "1.05rem",
      color: "#0b2f4a",
    },

    menu: {
      position: "absolute",
      right: 0,
      marginTop: "0.35rem",
      width: "min(320px, 92vw)",
      background: "#fff",
      border: "1px solid rgba(15,23,42,0.06)",
      boxShadow: "0 10px 30px rgba(2,6,23,0.12)",
      borderRadius: "0.6rem",
      padding: "0.5rem",
      zIndex: 300,
      fontSize: "0.95rem",
    },
    menuItem: {
      padding: "0.5rem 0.6rem",
      cursor: "pointer",
      borderRadius: "0.45rem",
      color: "#0b2540",
      transition: "background 0.15s ease, color 0.15s ease",
    },
    smallInput: {
      width: "140px",
      padding: "0.4rem 0.5rem",
      borderRadius: "0.45rem",
      border: "1px solid rgba(0,0,0,0.08)",
      marginRight: "0.45rem",
      fontSize: "0.9rem",
    },
    menuFooter: { marginTop: "0.5rem", borderTop: "1px dashed rgba(0,0,0,0.04)", paddingTop: "0.5rem", display:"none" },
  };

  function menuHoverProps() {
    return {
      onMouseEnter: (e) => {
        e.currentTarget.style.background = "#f1f5f9";
      },
      onMouseLeave: (e) => {
        e.currentTarget.style.background = "transparent";
      }
    };
  }

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <div style={styles.container}>
        {/* Add button (primary; icon + label on desktop) */}
        <button
          type="button"
          onClick={handleAddClick}
          title="Tambah"
          aria-label="Tambah peserta"
          style={styles.btnPrimary}
        >
          <img src={ICON_ADD} alt="" style={{...styles.iconOnly, filter: "invert(100%) brightness(100%)"}} />
          <span style={styles.labelDesktop}>Add</span>
        </button>

        {/* Scan button */}
        <button
          type="button"
          onClick={handleScanClick}
          title="Scan QR"
          aria-label="Scan QR"
          style={styles.btnBase}
        >
          <img src={ICON_SCAN} alt="" style={styles.iconOnly} />
          <span style={styles.labelDesktop}>Scan</span>
        </button>

        {!isMobile && (
          <>
          {/* Show/Hide Donasi toggle */}
          <button
            type="button"
            onClick={handleToggleDonation}
            title={showDonation ? "Hide Donasi" : "Show Donasi"}
            aria-pressed={!!showDonation}
            aria-label={showDonation ? "Sembunyikan kolom donasi" : "Tampilkan kolom donasi"}
            style={{
              ...styles.btnBase,
              ...(showDonation ? styles.btnActive : {}),
            }}
          >
            <img src={showDonation ? ICON_EYE_CROSS : ICON_EYE} alt="" style={{...styles.iconOnly, ...(showDonation ? {filter: "invert(100%) brightness(100%)"} : {}) }} />
            <span style={styles.labelDesktop}>{showDonation ? "Hide Donasi" : "Show Donasi"}</span>
          </button>

          <button
            type="button"
            onClick={handleRefresh}
            title="Refresh"
            aria-label="Refresh data"
            style={styles.btnBase}
          >
            <img src={ICON_REFRESH} alt="" style={styles.iconOnly} />
            <span style={styles.labelDesktop}>Refresh</span>
          </button>
          </>
        )}

        {/* More (matched size) */}
        <div ref={toggleRef} style={{ position: "relative" }}>
          <button
            type="button"
            aria-haspopup="true"
            aria-expanded={open}
            onClick={() => setOpen(o => !o)}
            style={styles.moreBtn}
            title="More"
            aria-label="More actions"
          >
            <span style={{ fontSize: 20, lineHeight: 1, transform: "translateY(-1px)" }}>⋯</span>
          </button>

          {open && (
            <div ref={menuRef} role="menu" style={styles.menu}>
              {isMobile && (
                <>
                <div
                  role="menuitem"
                  onClick={() => {
                    handleRefresh();
                    setOpen(false);
                  }}
                  onKeyPress={(e) => { if (e.key === "Enter") handleRefresh(); }}
                  {...menuHoverProps()}
                  style={styles.menuItem}
                >
                  Refresh Data
                </div>

                <div
                  role="menuitem"
                  onClick={() => {
                    handleToggleDonation();
                    setOpen(false);
                  }}
                  onKeyPress={(e) => { if (e.key === "Enter") handleToggleDonation(); }}
                  {...menuHoverProps()}
                  style={styles.menuItem}
                >
                  {showDonation ? "Hide Donasi" : "Show Donasi"}
                </div>
                </>
              )}

              <div
                role="menuitem"
                tabIndex={0}
                onClick={handleTogglePreReg}
                onKeyPress={(e) => { if (e.key === "Enter") handleTogglePreReg(); }}
                {...menuHoverProps()}
                style={{ ...styles.menuItem }}
              >
                {showPreReg ? "Hide Pre-Reg participants" : "Show Pre-Reg participants"}
              </div>

              <div style={{ margin: "0.5rem 0", borderBottom: "solid 1px #dedede" }}> </div>

              <AgendaCrossNavMenu
                agendaId={agendaId}
                hasPreRegForm={hasPreRegForm}
                onClose={()=>setShowMenu(false)}
              />

              <div style={{ margin: "0.5rem 0", borderBottom: "solid 1px #dedede" }}> </div>

              <div
                role="menuitem"
                tabIndex={2}
                onClick={handleExport}
                onKeyPress={(e) => { if (e.key === "Enter") handleExport(); }}
                {...menuHoverProps()}
                style={{ ...styles.menuItem, marginTop: "0.25rem" }}
              >
                Export Sheets
              </div>

              <div
                role="menuitem"
                tabIndex={3}
                onClick={handleGenerateReport}
                onKeyPress={(e) => { if (e.key === "Enter") handleGenerateReport(); }}
                {...menuHoverProps()}
                style={{ ...styles.menuItem, marginTop: "0.25rem" }}
              >
                Generate Laporan Kelas
              </div>

              <div style={{ margin: "0.5rem 0", borderBottom: "solid 1px #dedede" }}> </div>

              <div
                role="menuitem"
                tabIndex={1}
                onClick={()=>{
                  setOpen(false);
                  onOpenConfig && onOpenConfig();
                }}
                onKeyPress={(e) => { if (e.key === "Enter") handleTogglePreReg(); }}
                {...menuHoverProps()}
                style={{ ...styles.menuItem }}
              >
                <GearIcon/> Configuration
              </div>

              <div style={styles.menuFooter}>
                <div style={{ fontSize: "0.82rem", color: "#475569" }}>
                  &nbsp;
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Hidden inline "Add by code" form for keyboard users; kept hidden */}
      <div style={{ display: "none" }}>
        <form onSubmit={handleAddByCode} style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
          <input value={addCode} onChange={(e) => setAddCode(e.target.value)} placeholder="Kode (contoh: ZI15)" style={styles.smallInput} />
          <button type="submit" style={styles.btnBase}>Add</button>
        </form>
      </div>
    </div>
  );
}

PresensiTopHeaderControl.propTypes = {
  onAddById: PropTypes.func, // (id?) => void
  onScanQr: PropTypes.func,
  onToggleShowDonation: PropTypes.func,
  showDonation: PropTypes.bool,
  onTogglePreRegVisibility: PropTypes.func,
  showPreReg: PropTypes.bool,
  onExport: PropTypes.func,
  onGenerateReport: PropTypes.func,
};
